import type { AvailabilitySlot } from '@/lib/types/availability';
import { getErrorMessage } from '@/plugins/i18n/enums';
import { useAvailabilitySlotsStore } from '@/store/availability';
import { atEndOfDay, DAYS_OF_WEEK } from '@/utils/date';
import { useToast } from 'primevue/usetoast';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface SlotPendingDelete {
    slot: AvailabilitySlot;
    originalIndex: number;
    hasReservations: boolean;
    reason?: string;
}

export function useResourceAvailability() {
    const { t } = useI18n();
    const toast = useToast();
    const availabilityStore = useAvailabilitySlotsStore();

    const slots = ref<AvailabilitySlot[]>([]);
    const loading = ref(false);

    // Track slots pending deletion
    const slotsPendingDelete = ref<SlotPendingDelete[]>([]);
    const checkingDeletable = ref<string | null>(null); // slot id being checked

    const daysOfWeek = computed(() => DAYS_OF_WEEK.map(day => ({
        label: t(`days.${day.toLowerCase()}`),
        value: day
    })));

    const totalWeeklyHours = computed(() => {
        return slots.value
            .filter(s => !s.validFrom && !s.validUntil)
            .reduce((total, slot) => total + getSlotDuration(slot), 0);
    });

    function getDayLabel(dayOfWeek: string): string {
        const day = daysOfWeek.value.find(d => d.value === dayOfWeek);
        return day?.label || dayOfWeek;
    }

    function getSlotDuration(slot: AvailabilitySlot): number {
        const [startHour, startMin] = slot.start.split(':').map(Number);
        const [endHour, endMin] = slot.end.split(':').map(Number);
        const duration = (endHour * 60 + endMin - startHour * 60 - startMin) / 60;
        return Math.max(0, Math.round(duration * 10) / 10);
    }

    function getDateFromTimestamp(timestamp: number | null): Date | null {
        if (!timestamp) return null;
        return new Date(timestamp);
    }

    function updateSlotStartDate(index: number, date: Date | Date[] | (Date | null)[] | null | undefined) {
        if (date instanceof Date) {
            slots.value[index].validFrom = date.getTime();
        } else {
            slots.value[index].validFrom = null;
        }
    }

    function updateSlotEndDate(index: number, date: Date | Date[] | (Date | null)[] | null | undefined) {
        if (date instanceof Date) {
            slots.value[index].validUntil = atEndOfDay(date)?.getTime() || null;
        } else {
            slots.value[index].validUntil = null;
        }
    }

    function formatDate(timestamp: number | null): string {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    /**
     * Format slot time input on blur - handles inputs like "1430" -> "14:30"
     */
    function formatSlotTime(index: number, type: 'start' | 'end') {
        const field = type === 'start' ? 'start' : 'end';
        const input = slots.value[index][field];
        const formatted = formatTimeInput(input);
        if (formatted) {
            slots.value[index][field] = formatted;
        }
    }

    function formatTimeInput(input: string | null): string | null {
        if (!input) return null;

        // If already in correct format, keep it
        if (/^\d{2}:\d{2}$/.test(input)) {
            const [h, m] = input.split(':').map(Number);
            if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            }
        }

        // Remove any non-digit characters
        const digits = input.replace(/\D/g, '');
        if (digits.length === 0) return null;

        let hours: number;
        let minutes: number;

        if (digits.length === 1) {
            hours = parseInt(digits);
            minutes = 0;
        } else if (digits.length === 2) {
            hours = parseInt(digits);
            minutes = 0;
        } else if (digits.length === 3) {
            hours = parseInt(digits.substring(0, 1));
            minutes = parseInt(digits.substring(1, 3));
        } else {
            hours = parseInt(digits.substring(0, 2));
            minutes = parseInt(digits.substring(2, 4));
        }

        if (hours > 23) hours = 23;
        if (minutes > 59) minutes = 59;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    function parseTimeFromHHMMSS(time: string): string {
        if (time.match(/^\d{2}:\d{2}:\d{2}$/)) {
            return time.substring(0, 5);
        }
        return time;
    }

    async function loadAvailability(resourceId: string) {
        loading.value = true;
        try {
            await availabilityStore.fetchByResourceId(resourceId);
            const existingSlots = availabilityStore.getSlotsByResourceId(resourceId);

            if (existingSlots && existingSlots.slots.length > 0) {
                slots.value = existingSlots.slots.map(slot => ({
                    ...slot,
                    start: parseTimeFromHHMMSS(slot.start),
                    end: parseTimeFromHHMMSS(slot.end)
                }));
            } else {
                slots.value = [];
            }
        } catch (error) {
            console.error('Error loading availability:', error);
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: t('availability.validation.loadError'),
                life: 3000
            });
            slots.value = [];
        } finally {
            loading.value = false;
        }
    }

    function addNewSlot() {
        const newSlot: AvailabilitySlot = {
            title: t('availability.slotType.standard'),
            description: '',
            dayOfWeek: 'Monday',
            start: '09:00',
            end: '17:00',
            validFrom: null,
            validUntil: null
        };
        slots.value.push(newSlot);
    }

    function removeSlot(index: number) {
        slots.value.splice(index, 1);
    }

    /**
     * Check if a slot can be deleted without affecting reservations.
     * Returns true if deletable, false if has reservations.
     */
    async function checkSlotDeletable(slot: AvailabilitySlot): Promise<boolean> {
        if (!slot.id) {
            // New slot (not yet saved), can always be deleted
            return true;
        }

        checkingDeletable.value = slot.id;
        try {
            return await availabilityStore.isAvailabilitySlotDeletable(slot);
        } catch (error) {
            console.error('Error checking slot deletable:', error);
            // On error, assume it's not deletable (safer)
            return false;
        } finally {
            checkingDeletable.value = null;
        }
    }

    /**
     * Mark a slot for deletion. Called when user clicks delete.
     */
    function markSlotForDeletion(slot: AvailabilitySlot, originalIndex: number, hasReservations: boolean, reason?: string) {
        // Remove from active slots
        const slotIndex = slots.value.findIndex(s =>
            s.id ? s.id === slot.id :
                s.dayOfWeek === slot.dayOfWeek && s.start === slot.start && s.end === slot.end
        );

        if (slotIndex !== -1) {
            slots.value.splice(slotIndex, 1);
        }

        // Add to pending delete (only if it has an id - already saved slots)
        if (slot.id) {
            slotsPendingDelete.value.push({
                slot,
                originalIndex,
                hasReservations,
                reason
            });
        }
    }

    /**
     * Cancel a pending deletion, restore the slot to the list.
     */
    function cancelSlotDeletion(slotId: string) {
        const pendingIndex = slotsPendingDelete.value.findIndex(p => p.slot.id === slotId);
        if (pendingIndex !== -1) {
            const pending = slotsPendingDelete.value[pendingIndex];
            // Restore the slot to the list
            slots.value.push(pending.slot);
            slotsPendingDelete.value.splice(pendingIndex, 1);
        }
    }

    /**
     * Check if a slot is pending deletion.
     */
    function isSlotPendingDelete(slotId: string): boolean {
        return slotsPendingDelete.value.some(p => p.slot.id === slotId);
    }

    /**
     * Get pending delete info for a slot.
     */
    function getPendingDeleteInfo(slotId: string): SlotPendingDelete | undefined {
        return slotsPendingDelete.value.find(p => p.slot.id === slotId);
    }

    /**
     * Clear all pending deletions.
     */
    function clearPendingDeletions() {
        slotsPendingDelete.value = [];
    }

    function applyWeekdaysTemplate() {
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        const customSlots = slots.value.filter((s) => s.validFrom || s.validUntil);
        const weekdaySlots: AvailabilitySlot[] = weekdays.map((day) => ({
            title: t('availability.weekdays'),
            dayOfWeek: day,
            start: '09:00',
            end: '17:00',
            validFrom: null,
            validUntil: null
        }));
        slots.value = [...customSlots, ...weekdaySlots];
    }

    function applyFullWeekTemplate() {
        const allDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const customSlots = slots.value.filter(s => s.validFrom || s.validUntil);
        const fullWeekSlots: AvailabilitySlot[] = allDays.map((day) => ({
            title: t('availability.fullWeek'),
            dayOfWeek: day,
            start: '09:00',
            end: '17:00',
            validFrom: null,
            validUntil: null
        }));
        slots.value = [...customSlots, ...fullWeekSlots];
    }

    function clearAllSlots() {
        slots.value = [];
    }

    function validateSlots(): boolean {
        for (const slot of slots.value) {
            const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
            if (!timeRegex.test(slot.start) || !timeRegex.test(slot.end)) {
                toast.add({
                    severity: 'warn',
                    summary: t('attention'),
                    detail: t('availability.validation.invalidTime'),
                    life: 4000
                });
                return false;
            }

            if (slot.start >= slot.end) {
                toast.add({
                    severity: 'warn',
                    summary: t('attention'),
                    detail: t('availability.validation.startBeforeEnd'),
                    life: 4000
                });
                return false;
            }

            if (slot.validFrom && slot.validUntil) {
                if (slot.validFrom > slot.validUntil) {
                    toast.add({
                        severity: 'warn',
                        summary: t('attention'),
                        detail: t('availability.validation.validFromBeforeUntil'),
                        life: 4000
                    });
                    return false;
                }
            }
        }
        return true;
    }

    async function saveAvailability(resourceId: string): Promise<boolean> {
        if (!validateSlots()) return false;

        try {
            // First, delete any slots that are pending deletion
            const slotsToDelete: { id: string, reason?: string }[] = slotsPendingDelete.value
                .filter(p => p.slot.id)
                .map(p => ({
                    id: p.slot.id!,
                    reason: p.hasReservations ? p.reason : undefined
                }));

            await availabilityStore.deleteAvailabilitySlots(slotsToDelete);

            // Clear pending deletions after successful deletion
            slotsPendingDelete.value = [];

            const data = {
                resourceId,
                slots: slots.value.map(slot => ({
                    ...slot,
                    start: slot.start,
                    end: slot.end,
                    validFrom: slot.validFrom,
                    validUntil: slot.validUntil
                }))
            };

            const error = await availabilityStore.setAvailabilitySlots(data);
            if (error) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(error, t),
                    life: 5000
                });
                return false;
            }
            await availabilityStore.fetchByResourceId(resourceId);

            return true;
        } catch (error: any) {
            console.error('Error saving availability:', error);

            const errorMessage = error?.response?.data?.message
                || error?.message
                || t('availability.validation.saveError');

            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: errorMessage,
                life: 5000
            });

            return false;
        }
    }

    return {
        // State
        slots,
        loading,
        slotsPendingDelete,
        checkingDeletable,

        // Computed
        daysOfWeek,
        totalWeeklyHours,

        // Helpers
        getDayLabel,
        getSlotDuration,
        getDateFromTimestamp,
        updateSlotStartDate,
        updateSlotEndDate,
        formatDate,
        formatSlotTime,

        // Actions
        loadAvailability,
        addNewSlot,
        removeSlot,
        checkSlotDeletable,
        markSlotForDeletion,
        cancelSlotDeletion,
        isSlotPendingDelete,
        getPendingDeleteInfo,
        clearPendingDeletions,
        applyWeekdaysTemplate,
        applyFullWeekTemplate,
        clearAllSlots,
        saveAvailability
    };
}
